/**
 * The month a schedule draws, and the window of time it asks the server for.
 *
 * Pure calendar arithmetic, and shared: the MCP App's schedule shell draws the
 * same six-week grid, and a month shown in a chat and a month shown on this
 * page should be the same month. The shell buckets its rows by day itself
 * because it is handed a flat page; here the *server* buckets, and the two
 * agree about which days there are because they build the grid from this file.
 *
 * The grid is built in UTC throughout -- `Date.UTC`, `getUTCDate` -- but that is
 * not a timezone decision: these are calendar dates, and doing the arithmetic in
 * one fixed frame is what stops "the 1st" from being two different days
 * depending on where the browser is.
 */
import { TZDate } from '@date-fns/tz'

/** Monday first, which is what `monthGrid` lays out. */
export const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const

/** How many days a month grid holds: six weeks, so every month fits. */
export const GRID_DAYS = 42

/**
 * An instant as the calendar day it falls on, in a timezone.
 *
 * `null` means UTC, which is the same rule the date renderer follows: a
 * date-only value is UTC midnight, and reading it anywhere else lands it on the
 * day before, west of Greenwich.
 *
 * The format is the server's own: it keys each day of a schedule's answer by
 * `YYYY-MM-DD`, so this is how a grid cell finds its rows.
 */
export function dayKey(date: Date, timezone: string | null): string {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone ?? 'UTC',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(date)
  const of = (type: string) => parts.find((part) => part.type === type)?.value ?? ''
  return `${of('year')}-${of('month')}-${of('day')}`
}

/** The six weeks a month is drawn in, Monday first. */
export function monthGrid(month: Date): Date[] {
  const first = startOfMonth(month)
  // Monday is 0 here; `getUTCDay` calls Sunday 0.
  const lead = (first.getUTCDay() + 6) % 7
  const start = new Date(first)
  start.setUTCDate(first.getUTCDate() - lead)

  return Array.from({ length: GRID_DAYS }, (_, index) => {
    const day = new Date(start)
    day.setUTCDate(start.getUTCDate() + index)
    return day
  })
}

export function startOfMonth(date: Date): Date {
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1))
}

export function addMonths(month: Date, count: number): Date {
  return new Date(Date.UTC(month.getUTCFullYear(), month.getUTCMonth() + count, 1))
}

export function sameMonth(day: Date, month: Date): boolean {
  return (
    day.getUTCFullYear() === month.getUTCFullYear() &&
    day.getUTCMonth() === month.getUTCMonth()
  )
}

export function monthLabel(month: Date): string {
  return new Intl.DateTimeFormat('en-US', {
    timeZone: 'UTC',
    month: 'long',
    year: 'numeric',
  }).format(month)
}

/**
 * The instants a month's grid spans, in the timezone the rows are bucketed by.
 *
 * From midnight at the start of the first cell to midnight at the start of the
 * day after the last -- half open, which is what the endpoint documents
 * (`from_timestamp` inclusive, `to_timestamp` exclusive).
 *
 * Computed in the field's own timezone rather than in UTC, because the two are
 * different instants and the difference is a whole day's rows at each edge: a
 * grid asked for in UTC while the reader is in Auckland starts thirteen hours
 * late, so the first day of the month comes back empty.
 */
export function monthWindow(
  month: Date,
  timezone: string | null,
): { from: string; to: string } {
  const days = monthGrid(month)
  const first = days[0] as Date
  const last = days[days.length - 1] as Date

  const after = new Date(last)
  after.setUTCDate(last.getUTCDate() + 1)

  return { from: midnightAt(first, timezone), to: midnightAt(after, timezone) }
}

/** The instant a calendar day begins in a timezone, as the API spells it. */
function midnightAt(day: Date, timezone: string | null): string {
  return new Date(
    new TZDate(
      day.getUTCFullYear(),
      day.getUTCMonth(),
      day.getUTCDate(),
      0,
      0,
      0,
      timezone ?? 'UTC',
    ).getTime(),
  ).toISOString()
}
